/* eslint-env browser */
import PropTypes from "prop-types";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

const noop = () => {};
const PROBLEM_DISPLAY_MODE_STORAGE_KEY = "problemDisplayMode";

export const PROBLEM_DISPLAY_MODES = Object.freeze({
  normal: "normal",
  split: "split",
  shortForm: "short-form",
});

const normalizeProblemDisplayMode = (value) => {
  if (value === PROBLEM_DISPLAY_MODES.split) {
    return PROBLEM_DISPLAY_MODES.split;
  }
  if (value === PROBLEM_DISPLAY_MODES.shortForm) {
    return PROBLEM_DISPLAY_MODES.shortForm;
  }
  return PROBLEM_DISPLAY_MODES.normal;
};

const readStoredProblemDisplayMode = () => {
  if (typeof window === "undefined") {
    return PROBLEM_DISPLAY_MODES.normal;
  }

  try {
    const storedMode = window.localStorage.getItem(
      PROBLEM_DISPLAY_MODE_STORAGE_KEY,
    );
    return normalizeProblemDisplayMode(storedMode);
  } catch {
    return PROBLEM_DISPLAY_MODES.normal;
  }
};

const ProblemNavContext = createContext({
  problemNav: null,
  setProblemNav: noop,
  clearProblemNav: noop,
  problemDisplayMode: PROBLEM_DISPLAY_MODES.normal,
  setProblemDisplayMode: noop,
});

export const ProblemNavProvider = ({ children }) => {
  const [problemNav, setProblemNav] = useState(null);
  const [problemDisplayMode, setProblemDisplayModeState] = useState(
    readStoredProblemDisplayMode,
  );

  const clearProblemNav = useCallback(() => setProblemNav(null), []);
  const setProblemDisplayMode = useCallback((nextMode) => {
    setProblemDisplayModeState((currentMode) => {
      const resolvedMode =
        typeof nextMode === "function" ? nextMode(currentMode) : nextMode;
      const normalizedMode = normalizeProblemDisplayMode(resolvedMode);

      try {
        window.localStorage.setItem(
          PROBLEM_DISPLAY_MODE_STORAGE_KEY,
          normalizedMode,
        );
      } catch {
        // Ignore storage failures and keep the in-memory preference.
      }

      return normalizedMode;
    });
  }, []);

  const value = useMemo(
    () => ({
      problemNav,
      setProblemNav,
      clearProblemNav,
      problemDisplayMode,
      setProblemDisplayMode,
    }),
    [problemNav, clearProblemNav, problemDisplayMode, setProblemDisplayMode],
  );

  return (
    <ProblemNavContext.Provider value={value}>
      {children}
    </ProblemNavContext.Provider>
  );
};

ProblemNavProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useProblemNav = () => useContext(ProblemNavContext);
